import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon, PrinterIcon, CalendarDaysIcon,
  BanknotesIcon, ClipboardDocumentListIcon, ArrowsRightLeftIcon,
  ArrowUpCircleIcon, ClockIcon, XMarkIcon,
} from '@heroicons/react/24/outline';

const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtMonth = (m) => { if (!m) return '—'; const [y,mo] = m.split('-'); return new Date(Number(y), Number(mo)-1).toLocaleDateString('en-US',{month:'short',year:'numeric'}); };
const money    = (n) => `Rs. ${Number(n||0).toLocaleString('en-PK')}`;
const pctColor = (p) => p == null ? '#9094a8' : p >= 80 ? '#10c98e' : p >= 60 ? '#f59e0b' : '#ef4444';

const ROLE_LABELS = {
  teacher: 'Teacher', coordinator: 'Coordinator', finance: 'Finance',
  librarian: 'Librarian', hostel_member: 'Hostel Member',
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sh * { box-sizing:border-box; }
  .sh { font-family:'Sora',sans-serif; color:#1a1d2e; animation:shFade .4s ease both; }
  @keyframes shFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
  .sh-hdr { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .sh-hdr-l { display:flex; align-items:center; gap:12px; }
  .back-btn { width:38px; height:38px; border-radius:10px; border:1px solid #e0e4f0; background:white; display:flex; align-items:center; justify-content:center; cursor:pointer; flex-shrink:0; }
  .back-btn svg { width:18px; height:18px; stroke:#6b7194; stroke-width:2; }
  .sh-title { font-size:1.3rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .sh-sub   { font-size:.76rem; color:#9094a8; margin-top:2px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; } .btn-blue:hover { background:#2d5ef0; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-green { background:#10c98e; color:white; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }

  .profile-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; margin-bottom:1.2rem; display:flex; gap:16px; flex-wrap:wrap; align-items:center; }
  .profile-avatar { width:64px; height:64px; border-radius:16px; background:linear-gradient(135deg,#3b6ef8,#8b5cf6); display:flex; align-items:center; justify-content:center; font-size:1.4rem; font-weight:700; color:white; flex-shrink:0; }
  .profile-name { font-size:1.15rem; font-weight:700; color:#0f1123; }
  .profile-meta { font-size:.78rem; color:#9094a8; margin-top:3px; }
  .profile-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:10px 20px; margin-top:14px; width:100%; }
  .profile-field-label { font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .profile-field-val   { font-size:.82rem; color:#1a1d2e; margin-top:2px; }
  .status-chip { display:inline-flex; font-size:.68rem; font-weight:700; padding:3px 10px; border-radius:99px; }
  .status-chip.active { background:#ecfdf5; color:#10c98e; }
  .status-chip.left   { background:#fef2f2; color:#ef4444; }
  .role-chip { display:inline-flex; font-size:.68rem; font-weight:700; padding:3px 10px; border-radius:99px; background:#eef2ff; color:#3b6ef8; margin-left:8px; }

  .section { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1.2rem; }
  .section-hdr { display:flex; align-items:center; gap:10px; padding:14px 18px; border-bottom:1px solid #f0f2f8; }
  .section-icon { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .section-icon svg { width:16px; height:16px; stroke-width:1.8; }
  .section-title { font-size:.88rem; font-weight:700; color:#0f1123; }
  .section-body { padding:16px 18px; }
  .empty-note { font-size:.8rem; color:#9094a8; text-align:center; padding:1.5rem; }

  .timeline { display:flex; flex-direction:column; gap:0; }
  .tl-item { display:flex; gap:12px; padding:10px 0; border-bottom:1px solid #f3f5fb; }
  .tl-item:last-child { border-bottom:none; }
  .tl-dot { width:9px; height:9px; border-radius:50%; background:#3b6ef8; margin-top:5px; flex-shrink:0; }
  .tl-dot.exit-dot { background:#ef4444; }
  .tl-dot.reactivated-dot { background:#10c98e; }
  .tl-body { flex:1; }
  .tl-main { font-size:.82rem; font-weight:600; color:#1a1d2e; }
  .tl-meta { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .tl-note { font-size:.74rem; color:#6b7194; margin-top:3px; font-style:italic; }

  .att-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(140px,1fr)); gap:10px; }
  .att-card { border:1px solid #e8ebf4; border-radius:10px; padding:12px; text-align:center; }
  .att-year { font-size:.72rem; font-weight:700; color:#9094a8; margin-bottom:6px; }
  .att-pct  { font-size:1.3rem; font-weight:700; font-family:'JetBrains Mono',monospace; }
  .att-detail { font-size:.68rem; color:#9094a8; margin-top:4px; }

  .tbl-wrap { overflow-x:auto; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl th { padding:8px 10px; text-align:center !important; font-size:.63rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; border-bottom:1px solid #edf0f9; white-space:nowrap; }
  .tbl td { padding:9px 10px; font-size:.78rem; border-bottom:1px solid #f5f6fb; vertical-align:middle; text-align:center !important; }
  .tbl tr:last-child td { border-bottom:none; }
  .badge { display:inline-flex; font-size:.66rem; font-weight:600; padding:2px 8px; border-radius:99px; white-space:nowrap; }
  .badge.approved,.badge.paid,.badge.approved-paid { background:#ecfdf5; color:#10c98e; }
  .badge.rejected,.badge.overdue { background:#fef2f2; color:#ef4444; }
  .badge.pending,.badge.unpaid { background:#fffbeb; color:#f59e0b; }
  .badge.waived,.badge.approved-unpaid { background:#eef2ff; color:#3b6ef8; }
  .badge.pending_verification,.badge.approved-half { background:#f3f0ff; color:#8b5cf6; }

  .loading { display:flex; align-items:center; justify-content:center; height:300px; gap:10px; color:#9094a8; }
  .spinner { width:20px; height:20px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  .req-banner { background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:10px 14px; font-size:.78rem; color:#c2410c; margin-bottom:1.2rem; display:flex; align-items:center; gap:8px; flex-wrap:wrap; justify-content:space-between; }
  .req-status-pill { display:inline-flex; font-size:.7rem; font-weight:700; padding:3px 10px; border-radius:99px; }
  .req-status-pill.pending  { background:#fffbeb; color:#f59e0b; }
  .req-status-pill.approved { background:#ecfdf5; color:#10c98e; }
  .req-status-pill.rejected { background:#fef2f2; color:#ef4444; }

  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; padding:24px; width:100%; max-width:440px; box-shadow:0 20px 60px rgba(0,0,0,.2); }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; margin-bottom:14px; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; display:block; margin-bottom:6px; }
  .form-textarea { width:100%; font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; outline:none; resize:vertical; min-height:80px; }
  .form-textarea:focus { border-color:#3b6ef8; background:white; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:16px; }

  @media (max-width:768px) {
    .sh-title { font-size:1.1rem; }
    .profile-card { flex-direction:column; align-items:flex-start; }
  }
`;

function Section({ icon, iconBg, iconColor, title, empty, children }) {
  return (
    <div className="section">
      <div className="section-hdr">
        <div className="section-icon" style={{ background:iconBg, color:iconColor }}>{icon}</div>
        <div className="section-title">{title}</div>
      </div>
      <div className="section-body">
        {empty ? <div className="empty-note">Nothing on record yet.</div> : children}
      </div>
    </div>
  );
}

export default function StaffHistory({ role }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = role === 'admin';
  const apiPrefix = isAdmin ? '/admin' : '/coordinator';

  const staffRole  = searchParams.get('role') || '';
  const isPortalQS = searchParams.get('isPortal');
  const isPortal   = isPortalQS !== 'false';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myRequests, setMyRequests] = useState([]);
  const [showReqModal, setShowReqModal] = useState(false);
  const [reqReason, setReqReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`${apiPrefix}/staff-history/${id}`, { params: { role: staffRole || undefined, isPortal: isPortalQS } })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load staff history'))
      .finally(() => setLoading(false));
  }, [apiPrefix, id, staffRole, isPortalQS]);

  const loadRequests = useCallback(() => {
    if (isAdmin) return;
    api.get('/coordinator/requests').then(r => setMyRequests(r.data || [])).catch(() => {});
  }, [isAdmin]);

  useEffect(() => { load(); loadRequests(); }, [load, loadRequests]);

  const myDownloadRequests = myRequests.filter(r => r.type === 'staff_history_download' && String(r.payload?.staffId) === String(id) && String(r.payload?.isPortal) === String(isPortal));
  const approvedReq = myDownloadRequests.find(r => r.status === 'approved');
  const pendingReq   = myDownloadRequests.find(r => r.status === 'pending');
  const canDownload  = isAdmin || !!approvedReq;

  const submitDownloadRequest = async () => {
    if (!reqReason.trim()) return toast.error('Reason is required');
    setSubmitting(true);
    try {
      await api.post('/coordinator/requests', {
        type: 'staff_history_download',
        payload: { staffId: id, staffName: data?.staff?.name, role: staffRole, isPortal, reason: reqReason },
      });
      toast.success('Download request submitted — awaiting admin approval');
      setShowReqModal(false); setReqReason('');
      loadRequests();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit request'); }
    finally { setSubmitting(false); }
  };

  const handlePrint = () => {
    if (!data) return;
    const { staff, timeline, attendanceHistory, salaryHistory, leaveHistory } = data;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>${staff.name} — Full History</title>
      <style>
        body{font-family:Arial,sans-serif;color:#1a1d2e;padding:30px;font-size:13px;}
        h1{font-size:20px;margin-bottom:2px;} h2{font-size:15px;margin:22px 0 8px;border-bottom:2px solid #3b6ef8;padding-bottom:4px;}
        .meta{color:#666;font-size:12px;margin-bottom:14px;}
        table{width:100%;border-collapse:collapse;margin-bottom:10px;}
        th,td{border:1px solid #ddd;padding:6px 9px;text-align:left;font-size:12px;}
        th{background:#f5f6fb;}
        .grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:10px;}
        .field-label{font-size:10px;color:#888;text-transform:uppercase;} .field-val{font-size:13px;}
        @media print { body{padding:10px;} }
      </style></head><body>
        <h1>${staff.name}</h1>
        <div class="meta">${ROLE_LABELS[staff.role] || staff.role} · ${staff.isPortal ? 'Portal Staff' : 'Non-Portal Staff'} · Employee ID: ${staff.employeeId || '—'}</div>

        <h2>Profile</h2>
        <div class="grid">
          <div><div class="field-label">Email</div><div class="field-val">${staff.email || '—'}</div></div>
          <div><div class="field-label">CNIC</div><div class="field-val">${staff.cnic || '—'}</div></div>
          <div><div class="field-label">Mobile</div><div class="field-val">${staff.mobile || '—'}</div></div>
          <div><div class="field-label">Joining Date</div><div class="field-val">${fmtDate(staff.joiningDate)}</div></div>
          <div><div class="field-label">Monthly Salary</div><div class="field-val">${money(staff.monthlySalary)}</div></div>
          ${staff.department != null ? `<div><div class="field-label">Department</div><div class="field-val">${staff.department || '—'}</div></div>` : ''}
          ${staff.designation != null ? `<div><div class="field-label">Designation</div><div class="field-val">${staff.designation || '—'}</div></div>` : ''}
          ${staff.qualification != null ? `<div><div class="field-label">Qualification</div><div class="field-val">${staff.qualification || '—'}</div></div>` : ''}
          ${staff.subjects != null ? `<div><div class="field-label">Subjects</div><div class="field-val">${(staff.subjects||[]).join(', ') || '—'}</div></div>` : ''}
          <div style="grid-column:1/-1"><div class="field-label">Address</div><div class="field-val">${staff.address || '—'}</div></div>
        </div>
        ${!staff.isActive ? `<p><strong>${staff.exitType === 'terminated' ? 'Terminated' : 'Exited'}:</strong> ${fmtDate(staff.exitDate)} — ${staff.exitType || ''} ${staff.exitNote ? '('+staff.exitNote+')' : ''} ${staff.exitByName ? 'by '+staff.exitByName : ''}</p>` : ''}

        <h2>Employment Timeline</h2>
        ${timeline.length === 0 ? '<p>No employment timeline records.</p>' : `
          <table><thead><tr><th>Date</th><th>Event</th><th>Detail</th></tr></thead><tbody>
            ${timeline.map(t => `<tr><td>${fmtDate(t.date)}</td><td>${t.kind === 'joined' ? 'Joined' : t.kind === 'reactivated' ? 'Reactivated' : `Exited — ${t.exitType||''}`}</td><td>${t.kind==='exit' ? [t.note, t.by?('by '+t.by):''].filter(Boolean).join(' · ') : '—'}</td></tr>`).join('')}
          </tbody></table>`}

        <h2>Attendance History</h2>
        ${attendanceHistory.length === 0 ? '<p>No attendance records.</p>' : `
          <table><thead><tr><th>Year</th><th>Present</th><th>Absent</th><th>On Leave</th><th>Holiday</th><th>%</th></tr></thead><tbody>
            ${attendanceHistory.map(a => `<tr><td>${a.year}</td><td>${a.present}</td><td>${a.absent}</td><td>${a.onLeave}</td><td>${a.holiday}</td><td>${a.percentage!=null?a.percentage+'%':'—'}</td></tr>`).join('')}
          </tbody></table>`}

        <h2>Salary History</h2>
        ${salaryHistory.length === 0 ? '<p>No salary records.</p>' : `
          <table><thead><tr><th>Month</th><th>Net Salary</th><th>Days Present</th><th>Status</th><th>Finalized</th><th>Paid</th></tr></thead><tbody>
            ${salaryHistory.map(s => `<tr><td>${fmtMonth(s.month)}</td><td>${money(s.netSalary)}</td><td>${s.daysPresent}</td><td>${s.status}</td><td>${s.finalizedAt ? fmtDate(s.finalizedAt)+(s.finalizedBy?' by '+s.finalizedBy:'') : '—'}</td><td>${s.paidAt ? fmtDate(s.paidAt)+(s.paidBy?' by '+s.paidBy:'') : '—'}</td></tr>`).join('')}
          </tbody></table>`}

        <h2>Leave History</h2>
        ${leaveHistory.length === 0 ? '<p>No leave records.</p>' : `
          <table><thead><tr><th>Dates</th><th>Type</th><th>Reason</th><th>Status</th><th>Reviewed By</th><th>Admin Note</th></tr></thead><tbody>
            ${leaveHistory.map(l => `<tr><td>${fmtDate(l.startDate)} – ${fmtDate(l.endDate)}</td><td>${l.type||'—'}</td><td>${l.reason||'—'}</td><td>${l.status}</td><td>${l.reviewedBy||'—'}</td><td>${l.adminNote||'—'}</td></tr>`).join('')}
          </tbody></table>`}

        <script>window.onload = () => window.print();</script>
      </body></html>
    `);
    win.document.close();
  };

  if (loading) return (
    <div className="sh"><style>{css}</style>
      <div className="loading"><div className="spinner"/>Loading staff history…</div>
    </div>
  );

  if (!data) return (
    <div className="sh"><style>{css}</style>
      <div className="empty-note">Could not load this staff member's history.</div>
    </div>
  );

  const { staff, timeline, attendanceHistory, salaryHistory, leaveHistory } = data;

  return (
    <div className="sh"><style>{css}</style>
      <div className="sh-hdr">
        <div className="sh-hdr-l">
          <button className="back-btn" onClick={() => navigate(-1)}><ArrowLeftIcon/></button>
          <div>
            <div className="sh-title">Full Staff History</div>
            <div className="sh-sub">Complete record from joining to today</div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {!isAdmin && !canDownload && (
            <button className="btn btn-ghost" onClick={() => setShowReqModal(true)} disabled={!!pendingReq}>
              <ClockIcon/>{pendingReq ? 'Request Pending Approval' : 'Request Download/Print'}
            </button>
          )}
          <button className="btn btn-blue" onClick={handlePrint} disabled={!canDownload}>
            <PrinterIcon/>Download / Print
          </button>
        </div>
      </div>

      {!isAdmin && pendingReq && (
        <div className="req-banner">
          <span>📋 Your download request for this staff member is awaiting admin approval.</span>
          <span className="req-status-pill pending">Pending</span>
        </div>
      )}
      {!isAdmin && approvedReq && (
        <div className="req-banner" style={{ background:'#ecfdf5', borderColor:'#a7f3d0', color:'#065f46' }}>
          <span>✓ Your download request was approved — you can print/download this staff member's history.</span>
          <span className="req-status-pill approved">Approved</span>
        </div>
      )}

      <div className="profile-card">
        <div className="profile-avatar">{(staff.name||'?').split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)}</div>
        <div style={{ flex:1, minWidth:200 }}>
          <div className="profile-name">
            {staff.name}
            <span className="role-chip">{ROLE_LABELS[staff.role] || staff.role}</span>
            <span className={`status-chip ${staff.isActive?'active':'left'}`} style={{marginLeft:8}}>{staff.isActive ? 'Active' : 'Exited'}</span>
          </div>
          <div className="profile-meta">{staff.employeeId || 'No Employee ID'} · {staff.isPortal ? 'Portal Staff' : 'Non-Portal Staff'}</div>
          <div className="profile-grid">
            <div><div className="profile-field-label">Email</div><div className="profile-field-val">{staff.email || '—'}</div></div>
            <div><div className="profile-field-label">Employee ID</div><div className="profile-field-val">{staff.employeeId || '—'}</div></div>
            <div><div className="profile-field-label">CNIC</div><div className="profile-field-val">{staff.cnic || '—'}</div></div>
            <div><div className="profile-field-label">Mobile</div><div className="profile-field-val">{staff.mobile || '—'}</div></div>
            <div><div className="profile-field-label">Joining Date</div><div className="profile-field-val">{fmtDate(staff.joiningDate)}</div></div>
            <div><div className="profile-field-label">Monthly Salary</div><div className="profile-field-val">{money(staff.monthlySalary)}</div></div>
            {staff.department != null && <div><div className="profile-field-label">Department</div><div className="profile-field-val">{staff.department || '—'}</div></div>}
            {staff.designation != null && <div><div className="profile-field-label">Designation</div><div className="profile-field-val">{staff.designation || '—'}</div></div>}
            {staff.qualification != null && <div><div className="profile-field-label">Qualification</div><div className="profile-field-val">{staff.qualification || '—'}</div></div>}
            {staff.subjects != null && <div><div className="profile-field-label">Subjects</div><div className="profile-field-val">{(staff.subjects||[]).join(', ') || '—'}</div></div>}
            <div style={{ gridColumn:'1/-1' }}><div className="profile-field-label">Address</div><div className="profile-field-val">{staff.address || '—'}</div></div>
          </div>
          {!staff.isActive && (
            <div style={{ marginTop:10, fontSize:'.78rem', color:'#ef4444' }}>
              {staff.exitType === 'terminated' ? 'Terminated' : 'Exited'} on {fmtDate(staff.exitDate)} — {staff.exitType || '—'} {staff.exitNote ? `(${staff.exitNote})` : ''} {staff.exitByName ? `by ${staff.exitByName}` : ''}
            </div>
          )}
        </div>
      </div>

      <Section icon={<ArrowsRightLeftIcon/>} iconBg="#eef2ff" iconColor="#3b6ef8" title="Employment Timeline" empty={timeline.length === 0}>
        <div className="timeline">
          {timeline.map((t, i) => (
            <div key={i} className="tl-item">
              <div className={`tl-dot${t.kind==='exit'?' exit-dot':t.kind==='reactivated'?' reactivated-dot':''}`}/>
              <div className="tl-body">
                <div className="tl-main">
                  {t.kind === 'joined' ? 'Joined' : t.kind === 'reactivated' ? 'Reactivated' : `Exited — ${t.exitType || '—'}`}
                </div>
                <div className="tl-meta">{fmtDate(t.date)} {t.kind==='exit' && t.by ? `· by ${t.by}` : ''}</div>
                {t.kind==='exit' && t.note && <div className="tl-note">{t.note}</div>}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section icon={<CalendarDaysIcon/>} iconBg="#ecfdf5" iconColor="#10c98e" title="Attendance History" empty={attendanceHistory.length === 0}>
        <div className="att-grid">
          {attendanceHistory.map(a => (
            <div key={a.year} className="att-card">
              <div className="att-year">{a.year}</div>
              <div className="att-pct" style={{ color: pctColor(a.percentage) }}>{a.percentage != null ? `${a.percentage}%` : '—'}</div>
              <div className="att-detail">{a.present}P · {a.absent}A · {a.onLeave}OL · {a.holiday}H</div>
            </div>
          ))}
        </div>
      </Section>

      <Section icon={<BanknotesIcon/>} iconBg="#f3f0ff" iconColor="#8b5cf6" title="Salary History" empty={salaryHistory.length === 0}>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Month</th><th>Net Salary</th><th>Days Present</th><th>Status</th><th>Finalized</th><th>Paid</th></tr></thead>
            <tbody>
              {salaryHistory.map(s => (
                <tr key={s._id}>
                  <td>{fmtMonth(s.month)}</td>
                  <td>{money(s.netSalary)}</td>
                  <td>{s.daysPresent}</td>
                  <td><span className={`badge ${s.status}`}>{s.status}</span></td>
                  <td>{s.finalizedAt ? <>{fmtDate(s.finalizedAt)}{s.finalizedBy ? <div style={{fontSize:'.68rem',color:'#9094a8'}}>by {s.finalizedBy}</div> : null}</> : '—'}</td>
                  <td>{s.paidAt ? <>{fmtDate(s.paidAt)}{s.paidBy ? <div style={{fontSize:'.68rem',color:'#9094a8'}}>by {s.paidBy}</div> : null}</> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section icon={<ClipboardDocumentListIcon/>} iconBg="#fffbeb" iconColor="#f59e0b" title="Leave History" empty={leaveHistory.length === 0}>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Dates</th><th>Type</th><th>Reason</th><th>Status</th><th>Reviewed By</th><th>Admin Note</th></tr></thead>
            <tbody>
              {leaveHistory.map(l => (
                <tr key={l._id}>
                  <td>{fmtDate(l.startDate)} – {fmtDate(l.endDate)}</td>
                  <td>{l.type || '—'}</td>
                  <td>{l.reason || '—'}</td>
                  <td><span className={`badge ${l.status}`}>{l.status}</span></td>
                  <td>{l.reviewedBy || '—'}</td>
                  <td>{l.adminNote || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {showReqModal && createPortal(
        <div className="modal-backdrop" onClick={() => setShowReqModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Request Download / Print Access</div>
            <label className="form-label">Reason</label>
            <textarea className="form-textarea" required value={reqReason} onChange={e => setReqReason(e.target.value)} placeholder="Why do you need to download this staff member's full history?"/>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setShowReqModal(false)}><XMarkIcon/>Cancel</button>
              <button className="btn btn-green" onClick={submitDownloadRequest} disabled={submitting}>
                <ArrowUpCircleIcon/>{submitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
